import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import './PaymentPlansStaff.css';

/**
 * PAYMENT PLANS MANAGEMENT - Admin Interface
 */
function PaymentPlansManager({ embedded = false }) {
  const { user } = useAuth();
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [activeEnrollments, setActiveEnrollments] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    planName: '',
    description: '',
    installmentCount: 12,
    installmentFrequency: 'monthly',
    downPaymentPercentage: 0,
    processingFee: 0,
    discountPercentage: 0,
    autoApprove: true
  });

  useEffect(() => {
    loadTemplates();
    loadActiveEnrollments();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/payment-plans/templates/${user?.teamId}`, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadActiveEnrollments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/payment-plans/upcoming/${user?.teamId}?days=30`, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });
      setActiveEnrollments(response.data);
    } catch (error) {
      console.error('Error loading enrollments:', error);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${API_BASE_URL}/api/payment-plans/templates`, formData, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });

      toast.success('Payment plan template created!');
      setShowCreateModal(false);
      loadTemplates();
      
      // Reset form
      setFormData({
        planName: '',
        description: '',
        installmentCount: 12,
        installmentFrequency: 'monthly',
        downPaymentPercentage: 0,
        processingFee: 0,
        discountPercentage: 0,
        autoApprove: true
      });

    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create payment plan');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="payment-plans-container">
      <div className="page-header">
        {!embedded && <h1>💳 Payment Plans</h1>}
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          + Create Payment Plan
        </button>
      </div>

      {/* Payment Plan Templates */}
      <div className="card">
        <h2>Available Payment Plans</h2>
        
        {templates.length === 0 ? (
          <div className="empty-state">
            <p>No payment plans created yet</p>
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              Create Your First Plan
            </button>
          </div>
        ) : (
          <div className="plans-grid">
            {templates.map(plan => (
              <div key={plan.id} className="plan-card">
                <h3>{plan.plan_name}</h3>
                <p className="plan-description">{plan.description}</p>
                <div className="plan-details">
                  <div className="detail-item">
                    <span className="label">Installments:</span>
                    <span className="value">{plan.installment_count} {plan.installment_frequency}</span>
                  </div>
                  {plan.down_payment_percentage > 0 && (
                    <div className="detail-item">
                      <span className="label">Down Payment:</span>
                      <span className="value">{plan.down_payment_percentage}%</span>
                    </div>
                  )}
                  {plan.discount_percentage > 0 && (
                    <div className="detail-item">
                      <span className="label">Discount:</span>
                      <span className="value badge badge-green">{plan.discount_percentage}% off</span>
                    </div>
                  )}
                  {plan.processing_fee > 0 && (
                    <div className="detail-item">
                      <span className="label">Setup Fee:</span>
                      <span className="value">${plan.processing_fee}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Active Enrollments */}
      <div className="card">
        <h2>Active Payment Plans ({activeEnrollments.length})</h2>
        
        {activeEnrollments.length === 0 ? (
          <p className="empty-state">No athletes currently enrolled in payment plans</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Athlete</th>
                <th>Plan</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Next Payment</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {activeEnrollments.map(enrollment => (
                <tr key={enrollment.id}>
                  <td>
                    <strong>{enrollment.athlete_name}</strong>
                    <br />
                    <small>{enrollment.parent_email}</small>
                  </td>
                  <td>{enrollment.plan_name}</td>
                  <td>${enrollment.total_amount}</td>
                  <td>{enrollment.installments_paid}/{enrollment.total_installments}</td>
                  <td><strong>${enrollment.remaining_balance}</strong></td>
                  <td>
                    {enrollment.next_payment_date}
                    <br />
                    <small className={enrollment.days_until_next_payment <= 7 ? 'text-warning' : ''}>
                      ({enrollment.days_until_next_payment} days)
                    </small>
                  </td>
                  <td>
                    <span className={`badge badge-${enrollment.auto_charge ? 'green' : 'yellow'}`}>
                      {enrollment.auto_charge ? 'Auto-pay' : 'Manual'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Plan Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Payment Plan</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>×</button>
            </div>

            <form onSubmit={handleCreateTemplate}>
              <div className="form-group">
                <label>Plan Name *</label>
                <input
                  type="text"
                  name="planName"
                  value={formData.planName}
                  onChange={handleInputChange}
                  placeholder="e.g., 12-Month Tuition Plan"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Spread your payments over 12 months with no interest"
                  rows="2"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Number of Payments *</label>
                  <input
                    type="number"
                    name="installmentCount"
                    value={formData.installmentCount}
                    onChange={handleInputChange}
                    min="2"
                    max="24"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Payment Frequency *</label>
                  <select
                    name="installmentFrequency"
                    value={formData.installmentFrequency}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Down Payment %</label>
                  <input
                    type="number"
                    name="downPaymentPercentage"
                    value={formData.downPaymentPercentage}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.01"
                  />
                  <small>Percentage due upfront</small>
                </div>

                <div className="form-group">
                  <label>Processing Fee $</label>
                  <input
                    type="number"
                    name="processingFee"
                    value={formData.processingFee}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                  />
                  <small>One-time setup fee</small>
                </div>
              </div>

              <div className="form-group">
                <label>Discount % (Optional)</label>
                <input
                  type="number"
                  name="discountPercentage"
                  value={formData.discountPercentage}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                />
                <small>Discount for choosing this plan</small>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="autoApprove"
                    checked={formData.autoApprove}
                    onChange={handleInputChange}
                  />
                  <span>Auto-approve enrollments (no manual approval needed)</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Plan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentPlansManager;
